import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";
import PricingStory from "../storyComponents/PricingStory";
import ScheduleNowStory from "./ScheduleNowStory";
import SpeedStory from "./SpeedStory";
import LocationStory from "./LocationStory";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import PartnerStory from "./PartnerStory";
import EuropeStory from "./EuropeStory";
import Cookies from "universal-cookie";
import { RingLoader } from "@/_components";
import { utilities } from "@/_helpers";

const StoryWrap = styled(Grid, {
  shouldForwardProp: (prop) =>
    !["storyName", "isMobileOrTablet"].includes(prop),
})(({ storyName, isMobileOrTablet, activeStep }) => ({
  padding:
    storyName?.toLowerCase() === "speed" ||
    storyName?.toLowerCase() === "partner"
      ? "0px"
      : "24px",
  ...(storyName?.toLowerCase() === "europe" && activeStep === 0 && {
    paddingTop: "0px",
    paddingLeft: "0px",
    paddingRight: "0px",
  }),
  width: "100%",
  marginTop: isMobileOrTablet ? "56px" : "0px",
  // paddingTop:isMobileOrTablet ? "0px" : "24px",
}));

const StoryLoaderWrap = styled(Grid)(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  minHeight: "calc(100vh - 56px)",
  width: "100%",
}));

const Story = ({ phoneNumber, isLoaded }) => {
  const { storyName } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const cookies = useRef(new Cookies()).current;
  const [activeStep, setActiveStep] = useState(0);
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser"));
  const [enrollmentCodeData, setEnrollmentCodeData] = useState(
    () => cookies.get("lsEnrollmentCode") || {},
  );
  const resolvedEnrolleeStep = enrolleeUser?.step || enrolleeUser?.Step;
  const resolvedEnrolleeStepLower = resolvedEnrolleeStep?.toLowerCase();
  const hasSetInitialStep = useRef(false);
  const navigate = useNavigate();
  const [deviceNeeded, setDeviceNeeded] = useState(false);
  const [showVehicleStep, setShowVehicleStep] = useState(
    enrolleeUser?.clientTypeId !== 5,
  );
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const buildQuoteStep = JSON.parse(sessionStorage.getItem("buildQuoteStep"));

  useEffect(() => {
    const storedEnrolleeUser = JSON.parse(
      sessionStorage.getItem("enrolleeUser"),
    );

    if (
      storedEnrolleeUser &&
      (Object.prototype.hasOwnProperty.call(
        storedEnrolleeUser,
        "EnrollmentCodeId",
      ) ||
        Object.prototype.hasOwnProperty.call(
          storedEnrolleeUser,
          "EnrollmentCodeID",
        ))
    ) {
      sessionStorage.setItem(
        "enrolleeUser",
        JSON.stringify(
          utilities.normalizeEnrolleeSessionData(storedEnrolleeUser),
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (
      enrolleeUser?.story?.toLowerCase() == "pricing" &&
      storyName.toLowerCase() !== "pricing" &&
      resolvedEnrolleeStepLower === "vehicle"
    ) {
      let newData = {
        ...buildQuoteStep,
        skipBuildQuote: true,
        vehicleCheckPoint: false,
      };
      sessionStorage.setItem("buildQuoteStep", JSON.stringify(newData));
    } else if (
      (resolvedEnrolleeStepLower === "contact" ||
        resolvedEnrolleeStepLower === "violation") &&
      enrolleeUser?.story?.toLowerCase() == "pricing"
    ) {
      let newData = {
        ...buildQuoteStep,
        skipBuildQuote: false,
        vehicleCheckPoint: false,
      };
      sessionStorage.setItem("buildQuoteStep", JSON.stringify(newData));
      let updateData = {
        ...enrolleeUser,
        hasSeenBuildQuote: false,
      };

      sessionStorage.setItem(
        "enrolleeUser",
        JSON.stringify(utilities.normalizeEnrolleeSessionData(updateData)),
      );
    }
  }, []);

  const allSteps = [
    { id: "Welcome", name: t("steps.welcome") },
    { id: "Contact", name: t("steps.aboutYou") },
    { id: "Violation", name: t("steps.stateInformation") },
    { id: "Vehicle", name: t("steps.yourVehicle") },
    { id: "Build My Quote", name: t("steps.buildMyQuote") },

    { id: "Address", name: "address" },
    { id: "Id", name: "Id" },
    { id: "Lpp", name: "Lpp" },
    { id: "Ready", name: "Ready" },
    { id: "Schedule", name: "Schedule" },
    { id: "MyAccount", name: "MyAccount" },
    { id: "MyAccount", name: "MyAccount" },
    { id: "LearnMore", name: "LearnMore" },
    { id: "Driver", name: "Driver" },
    { id: "WhyIsa", name: "WhyIsa" },
    { id: "Pricing", name: "Pricing" },
    { id: "PartnerPricing", name: "Partner Pricing" },
    { id: "WrapUp", name: "WrapUp" },
    { id: "Confirm", name: "Confirm" },
    { id: "Device", name: "Device" },
    { id: "StateId", name: "StateId" },
  ];

  const storyStepMap = {
    pricing: ["Welcome", "Contact", "Violation", "Vehicle", "Build My Quote"],
    schedule: [
      "Welcome",
      "Contact",
      "Violation",
      "Address",
      "Id",
      "Vehicle",
      "Ready",
      "Schedule",
      "MyAccount",
    ],
    speed: [
      "Welcome",
      "Contact",
      "LearnMore",
      "Driver",
      "WhyIsa",
      "Pricing",
      "Vehicle",
      "Ready",
      "Schedule",
      "Confirm",
      "WrapUp",
    ],
    location: [
      "Schedule",
      // "Device",
      "Contact",
      // "Schedule",
      "Violation",
      "Vehicle",
      "StateId",
      "Address",
      "MyAccount",
    ],
    partner: [
      "Welcome",
      "Contact",
      "Violation",
      "Vehicle",
      "Address",
      "Id",
      "PartnerPricing",
      "Schedule",
      "MyAccount",
    ],
    europe: ["Welcome", "Contact", "Vehicle", "Address", "Schedule"],
  };

  const STORY_NORMALIZATION_MAP = {
    "isa volunteer": "speed",
    speed: "speed",

    "schedule now": "schedule",
    schedule: "schedule",

    pricing: "pricing",
    location: "location",
    guidance: "guidance",
    europe: "europe",
  };

  const normalizeStory = (story) => {
    if (!story) return null;
    const normalizedStory = String(story).trim().toLowerCase();
    return STORY_NORMALIZATION_MAP[normalizedStory] || normalizedStory;
  };

  const normalizedStoryName = normalizeStory(storyName);
  const normalizedEnrolleeStory = normalizeStory(
    enrolleeUser?.story || enrolleeUser?.Story,
  );
  const enrolleeDeviceType = String(
    enrolleeUser?.deviceType || enrolleeUser?.DeviceType || "",
  )
    .trim()
    .toLowerCase();
  const isPamEnrollee =
    Number(
      enrolleeUser?.clientTypeId ??
        enrolleeUser?.ClientTypeId ??
        enrolleeUser?.clientTypeID ??
        enrolleeUser?.ClientTypeID,
    ) === 5 || ["pam", "portable"].includes(enrolleeDeviceType);
  const isIsaEnrollee =
    Number(
      enrolleeUser?.clientTypeId ??
        enrolleeUser?.ClientTypeId ??
        enrolleeUser?.clientTypeID ??
        enrolleeUser?.ClientTypeID,
    ) === 6 || ["isa", "speed", "speedv"].includes(enrolleeDeviceType);
  const enrolleeVehicleId =
    enrolleeUser?.vehicleId ??
    enrolleeUser?.VehicleId ??
    enrolleeUser?.vehicleID ??
    enrolleeUser?.VehicleID ??
    enrolleeUser?.selectedVehicleID ??
    enrolleeUser?.selectedVehicleId ??
    enrolleeUser?.vehicleInfo?.vehicleId ??
    enrolleeUser?.vehicleInfo?.VehicleId;
  const hasVehicle =
    Number.isFinite(Number(enrolleeVehicleId)) && Number(enrolleeVehicleId) > 0;
  useEffect(() => {
    const handleEnrollmentCodeChange = ({ name, value }) => {
      if (name !== "lsEnrollmentCode") return;
      setEnrollmentCodeData(value || cookies.get("lsEnrollmentCode") || {});
    };

    cookies.addChangeListener(handleEnrollmentCodeChange);

    return () => {
      cookies.removeChangeListener(handleEnrollmentCodeChange);
    };
  }, [cookies]);

  useEffect(() => {
    if (
      normalizedStoryName &&
      normalizedStoryName !== storyName?.toLowerCase()
    ) {
      navigate(`/story/${normalizedStoryName}`, { replace: true });
    }
  }, [storyName]);

  // const normalizedStoryName = storyName?.toLowerCase();

  const selectedStepIds = storyStepMap[normalizedStoryName] || [];
  const storySteps = selectedStepIds
    .map((id) => allSteps.find((step) => step.id === id))
    .filter(Boolean);
  const enrollmentCodeId =
    enrollmentCodeData?.enrollmentCodeId ||
    enrollmentCodeData?.EnrollmentCodeId ||
    enrollmentCodeData?.EnrollmentCodeID;
  const requiresProgramInfo = Boolean(
    normalizedStoryName !== "speed" &&
      (!enrollmentCodeId ||
        utilities.isMissingOrDefaultEnrollmentCode(enrollmentCodeData)),
  );
  const canResolveEnrollmentCodeInProgramInfo = Boolean(
    normalizedStoryName === "location" && requiresProgramInfo,
  );
  const isWaitingForEnrollmentCode = Boolean(
    storyStepMap[normalizedStoryName] &&
      !enrollmentCodeId &&
      !canResolveEnrollmentCodeInProgramInfo,
  );

  useEffect(() => {
    if (!storyStepMap[normalizedStoryName]) {
      return navigate("/pick-a-story");
    }

    if (isWaitingForEnrollmentCode) {
      return;
    }

    if (hasSetInitialStep.current || storySteps.length === 0) {
      return;
    }

    if (canResolveEnrollmentCodeInProgramInfo) {
      const locationEntryStep = storyStepMap[normalizedStoryName]?.findIndex(
        (step) => step === "Schedule",
      );

      setActiveStep(locationEntryStep !== -1 ? locationEntryStep : 0);
      hasSetInitialStep.current = true;
      return;
    }

    if (resolvedEnrolleeStep) {
      // const enrolleeUserStory = enrolleeUser?.story ?? enrolleeUser?.Story;

      // if (enrolleeUserStory?.toLowerCase() === normalizedStoryName) {
      if (normalizedEnrolleeStory === normalizedStoryName) {
        const currentPricingStep = storyStepMap[normalizedStoryName]?.findIndex(
          (step) => step === resolvedEnrolleeStep,
        );
        const vehicleStep = storyStepMap[normalizedStoryName]?.findIndex(
          (step) => step === "Vehicle",
        );
        const programInfoStep = storyStepMap[normalizedStoryName]?.findIndex(
          (step) => step === "Violation",
        );
        const shouldResumeAtProgramInfo =
          requiresProgramInfo &&
          programInfoStep !== -1 &&
          currentPricingStep > programInfoStep;
        const shouldResumeSpeedAtVehicle =
          normalizedStoryName === "speed" &&
          resolvedEnrolleeStepLower === "schedule" &&
          !isPamEnrollee &&
          !hasVehicle &&
          vehicleStep !== -1;
        const shouldResumeIsaAtVehicle =
          normalizedStoryName !== "speed" &&
          isIsaEnrollee &&
          !hasVehicle &&
          vehicleStep !== -1 &&
          currentPricingStep > vehicleStep;

        setActiveStep(
          shouldResumeAtProgramInfo
            ? programInfoStep
            : shouldResumeSpeedAtVehicle || shouldResumeIsaAtVehicle
            ? vehicleStep
            : currentPricingStep !== -1
            ? normalizedStoryName === "partner" &&
              (resolvedEnrolleeStep === "Schedule" ||
                resolvedEnrolleeStep === "MyAccount")
              ? currentPricingStep - 1
              : currentPricingStep
            : 0,
        );
      } else {
        if (storyName?.toLowerCase() === "location") {
          setActiveStep(1);
        } else {
          setActiveStep(0);
        }
      }

      if (
        normalizedEnrolleeStory === "pricing" &&
        storyName?.toLowerCase() === "pricing" &&
        (resolvedEnrolleeStep === "Vehicle" ||
          (resolvedEnrolleeStep == "Violation" &&
            (enrolleeUser?.clientTypeId === 5 ||
              enrolleeUser?.deviceType?.toLowerCase() === "portable")))
      ) {
        const buildMyQuoteStep = storySteps.findIndex(
          (step) => step.id === "Build My Quote",
        );
        if (buildQuoteStep?.skipBuildQuote) {
          let updateData = {
            ...enrolleeUser,
            hasSeenBuildQuote: true,
          };

          sessionStorage.setItem(
            "enrolleeUser",
            JSON.stringify({ ...updateData }),
          );
        }

        const isPamAndViolation =
          resolvedEnrolleeStep == "Violation" &&
          (enrolleeUser?.clientTypeId === 5 ||
            enrolleeUser?.deviceType?.toLowerCase() === "portable");
        if (buildMyQuoteStep) {
          // comment for now
          // setActiveStep(
          //   isPamAndViolation ? buildMyQuoteStep - 1 : buildMyQuoteStep,
          // );
          setActiveStep(
            isPamAndViolation
              ? enrolleeUser?.clientId % 2 !== 0
                ? buildMyQuoteStep - 1
                : buildMyQuoteStep
              : buildMyQuoteStep,
          );
        }
      }
      // will be used later
      //  else {
      //   // when story changes
      //   if (enrolleeUser?.story && storyName?.toLowerCase() !== enrolleeUser?.story?.toLowerCase()) {
      //     let matchedStoryIndex = getLatestMatchingStep(
      //       enrolleeUser?.step,
      //       storyStepMap[enrolleeUser?.story?.toLowerCase()], //past story
      //       storyStepMap[normalizedStoryName] //new story
      //     );

      //     if (
      //       (storyName === "Pricing" || storyName === "pricing" ) &&
      //       matchedStoryIndex?.step === "Vehicle"
      //     ) {
      //       const buildMyQuoteStep = storySteps.findIndex(
      //         (step) => step.id === "Build My Quote"
      //       );
      //       if (buildMyQuoteStep) {
      //         setActiveStep(buildMyQuoteStep);
      //       }
      //     } else {
      //       setActiveStep(matchedStoryIndex?.index);
      //     }
      //   } else {
      //     const matchedStepIndex = storySteps.findIndex(
      //       (step) => step.id === (enrolleeUser.step || enrolleeUser?.Step)
      //     );
      //     setActiveStep(matchedStepIndex !== -1 ? matchedStepIndex : 0);
      //   }
      // }

      hasSetInitialStep.current = true;
    }
  }, [
    enrolleeUser,
    storySteps,
    resolvedEnrolleeStep,
    normalizedStoryName,
    isWaitingForEnrollmentCode,
    requiresProgramInfo,
    canResolveEnrollmentCodeInProgramInfo,
  ]);

  // will be used later
  // const getLatestMatchingStep = (step, pastStory, newStory) => {
  //   try{

  //   // if component in new and old story match then return it
  //   const newStoryCheck = newStory.includes(step);
  //   if (newStoryCheck) {
  //     return { index: newStory.indexOf(step), step: step };
  //   }

  //   let passStoryIndex = pastStory.indexOf(step);

  //   for (let i = passStoryIndex - 1; i >= 0; i--) {
  //     let currentStep = pastStory[i];
  //     if (newStory.includes(currentStep)) {
  //       return { index: newStory.indexOf(currentStep), step: currentStep };
  //     }
  //   }
  //   return { index: 0, step: 0 };
  //   }catch(error)
  //   {
  //   return { index: 0, step: 0 };
  //   }

  // };

  if (isWaitingForEnrollmentCode) {
    return (
      <StoryLoaderWrap>
        <RingLoader />
      </StoryLoaderWrap>
    );
  }

  return (
    <StoryWrap storyName={storyName} isMobileOrTablet={isMobileOrTablet} activeStep={activeStep}>
      {{
        pricing: (
          <PricingStory
            steps={storySteps
              .filter((step) =>
                showVehicleStep === true
                  ? true
                  : enrolleeUser?.clientTypeId === 5 ||
                      deviceNeeded === "Portable"
                    ? step.id != "Vehicle"
                    : true,
              )
              .map((step) => step.id)}
            stepLabels={storySteps
              .filter((step) =>
                showVehicleStep === true
                  ? true
                  : enrolleeUser?.clientTypeId === 5 ||
                      deviceNeeded === "Portable"
                    ? step.name != "Your Vehicle"
                    : true,
              )
              .map((step) => step.name)}
            // stepLabels={storySteps.map((step) => step.name)}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            hasSetInitialStep={hasSetInitialStep}
            setDeviceNeeded={setDeviceNeeded}
            setShowVehicleStep={setShowVehicleStep}
            phoneNumber={phoneNumber}
            isLoaded={isLoaded}
          />
        ),
        schedule: (
          <ScheduleNowStory
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            hasSetInitialStep={hasSetInitialStep}
            isLoaded={isLoaded}
          />
        ),
        speed: (
          <SpeedStory
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            hasSetInitialStep={hasSetInitialStep}
            isLoaded={isLoaded}
          />
        ),
        location: (
          <LocationStory
            isLoaded={isLoaded}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            hasSetInitialStep={hasSetInitialStep}
          />
        ),
        partner: (
          <PartnerStory
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            hasSetInitialStep={hasSetInitialStep}
            isLoaded={isLoaded}
          />
        ),
        europe: (
          <EuropeStory
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            hasSetInitialStep={hasSetInitialStep}
            isLoaded={isLoaded}
          />
        ),
      }[normalizedStoryName] || <p>{normalizedStoryName || ""}</p>}
    </StoryWrap>
  );
};

Story.propTypes = {
  phoneNumber: PropTypes.string,
  isLoaded: PropTypes.bool,
};

export default Story;
